import { PMDataTable } from "../../utils/PMTableView/PMDataTable";
import { CellLink } from "../../utils/PMTableView/CellLink";
import { ItemStatus, dateToString } from "../../Utils";
import { CaseTagSelector } from "../cases/CaseTagSelector";
import { TripletexSyncBadge } from "../tripletex/tripletex-sync-badge";
import { retryProjectTripletexSync } from "../../../api/project";
import {
  orderProjectsUnderParents,
  projectParentDepth,
} from "../../../utils/projectListHierarchy";
import { signedFinancialColorClass } from "../../../utils/signedFinancialColor";

const formatListCurrency = (amount) => {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  return new Intl.NumberFormat("no-NO", {
    style: "currency",
    currency: "NOK",
    maximumFractionDigits: 0,
  }).format(Number(amount));
};

const formatListPercentage = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `${Number(value).toFixed(1)}%`;
};

const resolveListProfit = (financials) => {
  if (!financials) return null;

  const profit = financials.profit;
  if (profit != null && !Number.isNaN(Number(profit))) {
    return Number(profit);
  }

  const contractSum = Number(financials.contract_sum);
  const totalCosts = Number(financials.total_costs);
  const invoicedAmount = Number(financials.invoiced_amount);

  if (!Number.isNaN(contractSum) && contractSum > 0 && !Number.isNaN(totalCosts)) {
    return contractSum - totalCosts;
  }
  if (!Number.isNaN(invoicedAmount) && !Number.isNaN(totalCosts)) {
    return invoicedAmount - totalCosts;
  }

  return null;
};

const financialCell = (value, formatter, { sizeClass = "text-[13px]" } = {}) => {
  const formatted = formatter(value);
  if (formatted === "—") {
    return <span className={`${sizeClass} tabular-nums text-slate-300`}>{formatted}</span>;
  }
  return (
    <span
      className={`${sizeClass} tabular-nums ${signedFinancialColorClass(value)}`}
    >
      {formatted}
    </span>
  );
};

const financialResultMarginCell = (financials) => {
  const profit = resolveListProfit(financials);
  const margin = financials?.margin;
  const profitText = formatListCurrency(profit);
  const marginText = formatListPercentage(margin);

  if (profitText === "—" && marginText === "—") {
    return <span className="text-[13px] tabular-nums text-slate-300">—</span>;
  }

  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      {profitText !== "—" &&
        financialCell(profit, formatListCurrency, { sizeClass: "text-[13px] font-medium" })}
      {marginText !== "—" &&
        financialCell(margin, formatListPercentage, { sizeClass: "text-[12px]" })}
    </div>
  );
};

// Full column definitions (with cell renderers + grouping). Exported so the
// list page can derive column metadata for the toolbar / useTableView hook.
export function ProjectSyncBadge({ item, compact = false, onSyncUpdated }) {
  const sync = item?.tripletex_sync;
  if (!sync?.enabled) return null;
  if (compact && (sync.state === "synced" || sync.state === "disabled")) {
    return null;
  }

  const handleRetry = item?.id
    ? async () => {
        const res = await retryProjectTripletexSync(item.id);
        if (res?.tripletex_sync) {
          onSyncUpdated?.(item.id, res.tripletex_sync);
        }
        return res;
      }
    : undefined;

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <TripletexSyncBadge sync={sync} interactive onRetry={handleRetry} />
    </div>
  );
}

function ProjectStatusTagsCell({ item, t, tagDefinitions, onTagsChange }) {
  const selectedIds = (item?.tag_definitions || []).map((tag) => tag.id);
  if (!onTagsChange) {
    const tags = item?.tag_definitions || [];
    if (!tags.length) {
      return <span className="text-slate-300">—</span>;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {tags.map((tag) => (
          <span
            key={tag.id}
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
            style={
              tag.color
                ? { backgroundColor: `${tag.color}30`, color: tag.color }
                : undefined
            }
          >
            {tag.name}
          </span>
        ))}
      </div>
    );
  }
  return (
    <div
      className="min-w-0"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <CaseTagSelector
        value={selectedIds}
        onChange={(ids) => onTagsChange(item, ids)}
        showBadges
        scopeModule="project"
        definitions={tagDefinitions}
        buttonLabel={t("project_list.column.status_tags")}
      />
    </div>
  );
}

export const getProjectColumns = (
  t,
  {
    showFinancialColumns = false,
    tagDefinitions = [],
    onTagsChange = null,
    onSyncUpdated = null,
  } = {}
) => {
  const baseColumns = [
  {
    key: "project_number",
    label: t("Number"),
    sortKey: "project_number",
    width: 130,
    always: true,
    cardRole: "title",
    cell: (item) => (
      <div className="flex flex-col gap-0.5">
        <CellLink
          to={item?.id ? `/dashboard/projects/${item.id}` : null}
          className="text-[13.5px] font-semibold tabular-nums text-blue-600"
        >
          {item.project_number || "—"}
        </CellLink>
        {item.is_offer && (
          <span className="inline-flex w-fit rounded-full bg-amber-100 px-2 py-px text-[11px] font-semibold text-amber-800">
            {t("project_list.badge.offer")}
          </span>
        )}
        <div className="mt-2">
          <ProjectSyncBadge item={item} compact onSyncUpdated={onSyncUpdated} />
        </div>
      </div>
    ),
  },
  {
    key: "name",
    label: t("Name"),
    sortKey: "name",
    width: 220,
    cardRole: "title",
    cell: (item) => (
      <div className="flex min-w-0 flex-col gap-0.5">
        <CellLink
          to={item?.id ? `/dashboard/projects/${item.id}` : null}
          className="truncate text-[13.5px] font-medium text-slate-800"
        >
          {item.name || item.displayTitle || "—"}
        </CellLink>
        {item.parent_project && (
          <span className="truncate text-[12px] text-slate-400">
            {t("project_list.sub_of")}:{" "}
            {item.parent_project.displayTitle ||
              item.parent_project.name ||
              item.parent_project.project_number}
          </span>
        )}
      </div>
    ),
  },
  {
    key: "customer",
    label: t("Customer"),
    sortKey: "customer",
    width: 180,
    groupable: true,
    cardRole: "meta",
    groupValue: (item) => item?.customer?.displayTitle || item?.customer?.name || "",
    groupLabel: (key) => key || t("table_view.no_value"),
    cell: (item) =>
      item.customer ? (
        <CellLink
          to={item?.customer?.id ? `/dashboard/customers/${item.customer.id}` : null}
          className="block max-w-[180px] truncate text-[13px] text-slate-600"
        >
          {item.customer.displayTitle || item.customer.name || "—"}
        </CellLink>
      ) : (
        <span className="text-slate-300">—</span>
      ),
  },
  {
    key: "active",
    label: t("Status"),
    sortKey: "active",
    width: 110,
    stopRowClick: true,
    groupable: true,
    cardRole: "status",
    groupValue: (item) => (item.active ? "active" : "inactive"),
    groupLabel: (key) => (key === "inactive" ? t("Inactive") : t("Active")),
    cell: (item) => <ItemStatus item={item} />,
  },
  {
    key: "status_tags",
    label: t("project_list.column.status_tags"),
    width: 180,
    stopRowClick: true,
    cell: (item) => (
      <ProjectStatusTagsCell
        item={item}
        t={t}
        tagDefinitions={tagDefinitions}
        onTagsChange={onTagsChange}
      />
    ),
  },
  {
    key: "created_at",
    label: t("Created"),
    sortKey: "created_at",
    width: 120,
    defaultVisible: false,
    cell: (item) => (
      <span className="text-[13px] tabular-nums text-slate-500">
        {item.created_at ? dateToString(item.created_at) : "—"}
      </span>
    ),
  },
  ];

  const financialColumns = showFinancialColumns
    ? [
        {
          key: "financial_contract_sum",
          label: t("project_list.financials.contract_sum"),
          width: 130,
          defaultVisible: true,
          cell: (item) =>
            financialCell(item.financials?.contract_sum, formatListCurrency),
        },
        {
          key: "financial_invoiced_amount",
          label: t("project_list.financials.invoiced"),
          width: 130,
          defaultVisible: true,
          cell: (item) =>
            financialCell(item.financials?.invoiced_amount, formatListCurrency),
        },
        {
          key: "financial_total_costs",
          label: t("project_list.financials.costs"),
          width: 130,
          defaultVisible: true,
          cell: (item) =>
            financialCell(item.financials?.total_costs, formatListCurrency),
        },
        {
          key: "financial_result_margin",
          label: t("project_list.financials.result_margin"),
          width: 130,
          defaultVisible: true,
          cell: (item) => financialResultMarginCell(item.financials),
        },
        {
          key: "financial_remaining_amount",
          label: t("project_list.financials.remaining"),
          width: 130,
          defaultVisible: true,
          cell: (item) =>
            financialCell(item.financials?.remaining_amount, formatListCurrency),
        },
      ]
    : [];

  return [...baseColumns, ...financialColumns];
};

export const ProjectsTable = ({
  itemState = { data: [], isLoading: false },
  columns,
  visibleKeys,
  widths,
  onResize,
  groupBy = "none",
  sort = null,
  onSort,
  density = "comfortable",
  hasMore = false,
  onLoadMore,
  onItemsSelect = () => {},
  onRowClick,
  layout,
  hideSelection = false,
}) => {
  const { data = [], isLoading } = itemState;
  return (
    <PMDataTable
      columns={columns}
      visibleKeys={visibleKeys}
      widths={widths}
      onResize={onResize}
      groupBy={groupBy}
      sort={sort}
      onSort={onSort}
      data={data}
      isLoading={isLoading}
      density={density}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      onSelectionChange={onItemsSelect}
      onRowClick={onRowClick}
      reorderRows={orderProjectsUnderParents}
      rowIndent={(item) => projectParentDepth(item, data) * 20}
      mobileRowLayout
      layout={layout}
      hideSelection={hideSelection}
    />
  );
};

ProjectsTable.defaultProps = {
  itemState: { data: [], isLoading: false },
  density: "comfortable",
  hasMore: false,
};

export default ProjectsTable;
